import React from "react";
import { Bell, CheckCheck, Newspaper, Info, AlertTriangle, Sparkles } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { http } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const TYPE_ICON = { news: Newspaper, info: Info, warning: AlertTriangle, update: Sparkles };
const TYPE_COLOR = { news: "#22C55E", info: "#3B82F6", warning: "#EAB308", update: "#22C55E" };

export function NotificationBell() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { data } = useQuery({
    queryKey: ["notifications"],
    queryFn: async () => (await http.get("/notifications")).data,
    refetchInterval: 30000,
  });
  const readOne = useMutation({
    mutationFn: (id) => http.post(`/notifications/${id}/read`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notifications"] }),
  });
  const readAll = useMutation({
    mutationFn: () => http.post("/notifications/read-all"),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notifications"] }),
  });

  const items = data?.items || [];
  const unread = data?.unread || 0;
  const newItems = items.filter((n) => !n.read);
  const readItems = items.filter((n) => n.read);

  const openNotif = (n) => {
    if (!n.read) readOne.mutate(n.id);
    if (n.link) navigate(n.link);
  };

  const Row = ({ n }) => {
    const Icon = TYPE_ICON[n.type] || Bell;
    const color = TYPE_COLOR[n.type] || "#A1A1AA";
    return (
      <button onClick={() => openNotif(n)} data-testid="notif-item"
        className={`flex w-full items-start gap-3 border-b border-white/[0.05] px-4 py-3 text-left transition-colors last:border-0 hover:bg-white/[0.03] ${!n.read ? "bg-white/[0.02]" : ""}`}>
        <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg" style={{ backgroundColor: `${color}1a` }}>
          <Icon className="h-3.5 w-3.5" style={{ color }} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className={`truncate text-sm ${!n.read ? "font-semibold text-foreground" : "font-medium text-foreground/80"}`}>{n.title}</span>
            {!n.read && <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-[#22C55E]" />}
          </div>
          <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">{n.message}</p>
          <p className="mt-1 text-[11px] text-muted-foreground/60">{fmtDateTime(n.created_at)}</p>
        </div>
      </button>
    );
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button data-testid="notif-bell" className="relative rounded-lg p-2 transition-colors hover:bg-white/[0.04]">
          <Bell style={{ width: 18, height: 18 }} className="text-muted-foreground" />
          {unread > 0 && <span data-testid="notif-unread" className="absolute right-1 top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-[#EF4444] px-1 text-[10px] font-bold text-white">{unread > 9 ? "9+" : unread}</span>}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-96 border-white/[0.1] bg-[#1a1a1e] p-0">
        <div className="flex items-center justify-between border-b border-white/[0.08] px-4 py-2.5">
          <span className="text-sm font-medium text-foreground">Notifications</span>
          {unread > 0 && (
            <button onClick={() => readAll.mutate()} data-testid="notif-read-all" className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
              <CheckCheck className="h-3.5 w-3.5" /> Mark all read
            </button>
          )}
        </div>
        <div className="max-h-96 overflow-y-auto">
          {items.length === 0 && <p className="px-4 py-8 text-center text-sm text-muted-foreground">You're all caught up.</p>}
          {newItems.length > 0 && (
            <>
              <div className="bg-white/[0.02] px-4 py-1.5 text-[10px] font-semibold uppercase tracking-widest text-[#22C55E]" data-testid="notif-section-new">New · {newItems.length}</div>
              {newItems.map((n) => <Row key={n.id} n={n} />)}
            </>
          )}
          {readItems.length > 0 && (
            <>
              <div className="px-4 py-1.5 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/60" data-testid="notif-section-read">Earlier</div>
              {readItems.map((n) => <Row key={n.id} n={n} />)}
            </>
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
